"use client";

import { useLingui } from "@lingui/react/macro";
import {
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type SpeechInputErrorReason,
  useSpeechInput,
} from "@/hooks/use-speech-input";
import { useToast } from "@/hooks/use-toast";
import { useAiChatUiContext } from "@/lib/ai/ai-chat-ui-context";
import {
  type AnalysisTarget,
  getAnalysisTargetKey,
} from "@/lib/ai/analysis-target";
import type { AiModelAccess } from "@/lib/ai/model-access";
import type { ModelId } from "@/lib/ai/models";
import { cn } from "@/lib/utils";
import { AiModelUpgradeDialog } from "./ai-model-upgrade-dialog";
import type { AnalysisTargetInlineMentionMetadata } from "./analysis-target-display";
import {
  type AnalysisTargetOptionsState,
  getAnalysisTargetOptionKey,
} from "./analysis-target-options";
import {
  ChatPromptSurfaceContext,
  type ChatPromptSurfaceContextValue,
} from "./chat-prompt-surface-context";
import type { ChatQuotaTierIndicatorState } from "./chat-quota-view-state";
import {
  CHAT_SEND_ATTACHMENT_MAX_COUNT,
  type ChatSendAttachment,
  type ChatSendFileValidationError,
  type ChatSendInput,
  convertAcceptedFilesToChatSendAttachments,
  resolveChatSendText,
  validateChatSendFiles,
  validateChatSendTextFileEncodings,
} from "./chat-send-input";

export { ChatPromptSurfaceInput } from "./chat-prompt-surface-input";
export {
  ChatPromptSurfaceSuggestions,
  ChatPromptSurfaceTitle,
} from "./chat-prompt-surface-slots";

import { getSpeechSeparator } from "./chat-speech-text";
import { useAnalysisTargetMentionInput } from "./use-analysis-target-mention-input";
import { useChatPromptExpansion } from "./use-chat-prompt-expansion";

type ChatPromptSurfaceProps = {
  onSubmit: (
    message: ChatSendInput,
  ) => Promise<boolean | void> | boolean | void;
  model: ModelId;
  aiModelAccess: AiModelAccess;
  onModelChange: (model: ModelId) => void;
  pathname: string;
  optionsState: AnalysisTargetOptionsState;
  suppressPageContextAutoSyncOnCurrentPath: () => void;
  children: ReactNode;
  className?: string;
  expandedClassName?: string;
  compactClassName?: string;
  isInteractive?: boolean;
  submitBlocked?: boolean;
  compactWhenInactive?: boolean;
  keepExpanded?: boolean;
  lowQuotaIndicator: ChatQuotaTierIndicatorState;
  highQuotaIndicator: ChatQuotaTierIndicatorState;
  restoreFocusAfterSubmit?: boolean;
};

/**
 * starter screen と thread composer の両方で使う AI chat 入力 UI の root です。
 *
 * この component は送信可否、mention metadata の送信、送信成功後の draft 消去、
 * focus 復元、quota を考慮した model picker 制御を所有します。`onSubmit` が
 * reject した場合は、利用側が失敗を表示して再送できるように draft text と
 * mention を残します。見出しや候補プロンプトなどの画面固有 UI は子 component として
 * 合成し、送信や model picker を別経路で実装しないでください。
 */
export function ChatPromptSurface({
  onSubmit,
  model,
  aiModelAccess,
  onModelChange,
  pathname,
  optionsState,
  suppressPageContextAutoSyncOnCurrentPath,
  children,
  className,
  expandedClassName,
  compactClassName,
  isInteractive = true,
  submitBlocked = false,
  compactWhenInactive,
  keepExpanded,
  lowQuotaIndicator,
  highQuotaIndicator,
  restoreFocusAfterSubmit = false,
}: ChatPromptSurfaceProps) {
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<ChatSendAttachment[]>([]);
  const [attachmentErrors, setAttachmentErrors] = useState<
    ChatSendFileValidationError[]
  >([]);
  const [isAttachmentDropActive, setIsAttachmentDropActive] = useState(false);
  const [
    pendingAttachmentConversionCount,
    setPendingAttachmentConversionCount,
  ] = useState(0);
  const fileInputId = useId();
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentDragDepthRef = useRef(0);
  const attachmentIdRef = useRef(0);
  const shouldRestoreTextareaFocusRef = useRef(false);
  const {
    analysisTargets,
    pageAnalysisTarget,
    manualAnalysisTargets,
    addAnalysisTarget,
    removeAnalysisTarget,
    removeLastAnalysisTarget,
    setPageAnalysisTarget,
  } = useAiChatUiContext();
  const optionMetadataByKey = useMemo<
    ReadonlyMap<string, AnalysisTargetInlineMentionMetadata>
  >(
    () =>
      new Map(
        optionsState.options.map((option) => [
          getAnalysisTargetOptionKey(option.target),
          {
            projectTypeLabel: option.projectTypeLabel,
            detail: option.detail,
          },
        ]),
      ),
    [optionsState.options],
  );
  const canSubmit =
    isInteractive &&
    !submitBlocked &&
    pendingAttachmentConversionCount === 0 &&
    (input.trim().length > 0 || attachments.length > 0);
  const {
    mentionInstances,
    mentionedTargets,
    mentionMatch,
    mentionAnchor,
    mentionMenuOpen,
    mentionActiveOptionKey,
    setMentionActiveOptionKey,
    handleMentionTargetSelect,
    handleTextareaChange,
    handleTextareaKeyDown,
    handleTextareaKeyUp,
    handleTextareaClick,
    resetMentionState,
    clearMentionInstances,
    removeMentionTarget,
    textareaElement,
    bindTextarea,
  } = useAnalysisTargetMentionInput({
    isInteractive,
    input,
    setInput,
    pathname,
    pageContextSyncEnabled: false,
    externalSuppressPageContextAutoSyncOnCurrentPath:
      suppressPageContextAutoSyncOnCurrentPath,
    optionsState,
    selectedTargets: analysisTargets,
    hasRemovableSelectedTarget: analysisTargets.length > 0,
    setPageSelectedTarget: setPageAnalysisTarget,
    addSelectedTarget: addAnalysisTarget,
    removeLastSelectedTarget: removeLastAnalysisTarget,
  });
  const hasDraftInput = input.length > 0 || attachments.length > 0;
  const showInputOverlay = Boolean(
    mentionInstances.length > 0 && input.length > 0 && textareaElement,
  );
  const {
    containerRef,
    handleBlurCapture,
    handleFocusCapture,
    handleModelValueChange,
    isExpanded,
    isUpgradeDialogOpen,
    setContextPickerOpen,
    setModelSelectOpen,
    setUpgradeDialogOpen,
    textareaRows,
  } = useChatPromptExpansion({
    hasDraftInput,
    mentionMenuOpen,
    aiModelAccess,
    onModelChange,
    compactWhenInactive,
    keepExpanded,
  });
  const resolveTextarea = useCallback(() => {
    return containerRef.current?.querySelector<HTMLTextAreaElement>(
      'textarea[name="message"]',
    );
  }, [containerRef]);

  const { t, i18n } = useLingui();
  const { toast } = useToast();
  const speechLang = i18n.locale.startsWith("ja") ? "ja-JP" : "en-US";

  const handleSpeechResult = useCallback(
    (delta: string) => {
      // 確定した発話を既存入力の末尾へ追記する。stale を避けるため関数更新で最新値に連結する。
      setInput((previous) => {
        const separator = getSpeechSeparator(previous, delta, i18n.locale);
        return `${previous}${separator}${delta}`;
      });
    },
    [i18n.locale],
  );

  const handleSpeechError = useCallback(
    (reason: SpeechInputErrorReason) => {
      // 無音・ユーザーによる中断は通常操作の範囲なので通知しない。
      if (reason === "no-speech" || reason === "aborted") {
        return;
      }
      const description =
        reason === "not-allowed"
          ? t`Microphone access was denied. Please allow microphone access in your browser settings.`
          : reason === "audio-capture"
            ? t`No microphone was found. Please check your device.`
            : t`Voice input failed. Please try again.`;
      toast({
        title: t`Voice input error`,
        description,
        variant: "destructive",
      });
    },
    [t, toast],
  );

  const {
    isSupported: speechSupported,
    isListening: speechIsListening,
    interimTranscript: speechInterimTranscript,
    abort: abortSpeechRecognition,
    toggle: toggleSpeechRecognition,
  } = useSpeechInput({
    lang: speechLang,
    onResult: handleSpeechResult,
    onError: handleSpeechError,
  });

  const toggleSpeech = useCallback(() => {
    toggleSpeechRecognition();
  }, [toggleSpeechRecognition]);

  useEffect(() => {
    if (!isInteractive) {
      abortSpeechRecognition();
    }
  }, [abortSpeechRecognition, isInteractive]);

  const handleSubmit = useCallback(
    async (msg: { text?: string }) => {
      const text = resolveChatSendText({
        submittedText: msg.text,
        fallbackText: input,
      });
      if (!text && attachments.length === 0) return;
      if (
        !isInteractive ||
        submitBlocked ||
        pendingAttachmentConversionCount > 0
      ) {
        return;
      }
      // 送信後に遅れて届く transcript が、消去済み draft を再変更しないよう先に中断する。
      abortSpeechRecognition();
      if (restoreFocusAfterSubmit) {
        const textarea = resolveTextarea();
        shouldRestoreTextareaFocusRef.current =
          textarea !== undefined && textarea !== null
            ? document.activeElement === textarea
            : false;
      }
      const submitResult = await onSubmit({
        text: text ?? "",
        mentions: mentionInstances,
        ...(attachments.length > 0 ? { attachments } : {}),
      });
      if (submitResult === false) {
        return;
      }
      setInput("");
      setAttachments([]);
      setAttachmentErrors([]);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
      clearMentionInstances();
      resetMentionState();
    },
    [
      attachments,
      abortSpeechRecognition,
      clearMentionInstances,
      input,
      isInteractive,
      mentionInstances,
      onSubmit,
      pendingAttachmentConversionCount,
      resetMentionState,
      resolveTextarea,
      restoreFocusAfterSubmit,
      submitBlocked,
    ],
  );

  const handleAttachmentButtonClick = useCallback(() => {
    attachmentInputRef.current?.click();
  }, []);

  const addAttachmentFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const remainingSlots =
        CHAT_SEND_ATTACHMENT_MAX_COUNT - attachments.length;
      if (remainingSlots <= 0) {
        setAttachmentErrors([
          {
            code: "max-count",
            maxCount: CHAT_SEND_ATTACHMENT_MAX_COUNT,
          },
        ]);
        return;
      }

      const { acceptedFiles, errors } = validateChatSendFiles(
        files.slice(0, remainingSlots),
      );
      if (files.length > remainingSlots) {
        errors.push({
          code: "max-count",
          maxCount: CHAT_SEND_ATTACHMENT_MAX_COUNT,
        });
      }
      setAttachmentErrors(errors);

      if (acceptedFiles.length > 0) {
        setPendingAttachmentConversionCount((current) => current + 1);
        try {
          const { acceptedFiles: utf8AcceptedFiles, errors: encodingErrors } =
            await validateChatSendTextFileEncodings(acceptedFiles);
          if (encodingErrors.length > 0) {
            setAttachmentErrors([...errors, ...encodingErrors]);
          }
          if (utf8AcceptedFiles.length === 0) return;

          const nextAttachments =
            await convertAcceptedFilesToChatSendAttachments(
              utf8AcceptedFiles,
              () => {
                attachmentIdRef.current += 1;
                return `chat-attachment-${attachmentIdRef.current}`;
              },
            );
          setAttachments((current) => {
            const remainingSlotsAtAppend = Math.max(
              CHAT_SEND_ATTACHMENT_MAX_COUNT - current.length,
              0,
            );
            return [
              ...current,
              ...nextAttachments.slice(0, remainingSlotsAtAppend),
            ];
          });
        } finally {
          setPendingAttachmentConversionCount((current) =>
            Math.max(current - 1, 0),
          );
        }
      }
    },
    [attachments.length],
  );

  const handleAttachmentChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(event.target.files ?? []);
      await addAttachmentFiles(selectedFiles);
      event.target.value = "";
    },
    [addAttachmentFiles],
  );

  const resetAttachmentDragState = useCallback(() => {
    attachmentDragDepthRef.current = 0;
    setIsAttachmentDropActive(false);
  }, []);

  const handleAttachmentDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      if (!isInteractive) return;

      attachmentDragDepthRef.current += 1;
      setIsAttachmentDropActive(true);
    },
    [isInteractive],
  );

  const handleAttachmentDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();

      attachmentDragDepthRef.current = Math.max(
        attachmentDragDepthRef.current - 1,
        0,
      );
      if (attachmentDragDepthRef.current === 0) {
        setIsAttachmentDropActive(false);
      }
    },
    [],
  );

  const handleAttachmentDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    },
    [],
  );

  const handleAttachmentDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      resetAttachmentDragState();

      if (!isInteractive) return;
      const droppedFiles = Array.from(event.dataTransfer.files ?? []);
      void addAttachmentFiles(droppedFiles);
    },
    [addAttachmentFiles, isInteractive, resetAttachmentDragState],
  );

  const handleAttachmentRemove = useCallback((attachmentId: string) => {
    setAttachments((current) =>
      current.filter((attachment) => attachment.id !== attachmentId),
    );
  }, []);

  const handleAttachmentErrorDismiss = useCallback(
    (error: ChatSendFileValidationError) => {
      setAttachmentErrors((current) =>
        current.filter((currentError) => currentError !== error),
      );
    },
    [],
  );

  const handleAttachmentPaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const pastedFiles = Array.from(event.clipboardData?.items ?? [])
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);

      if (pastedFiles.length === 0) return;

      event.preventDefault();
      void addAttachmentFiles(pastedFiles);
    },
    [addAttachmentFiles],
  );

  useEffect(() => {
    if (!restoreFocusAfterSubmit || !isInteractive) {
      return;
    }
    if (!shouldRestoreTextareaFocusRef.current) {
      return;
    }

    const textarea = resolveTextarea();
    const activeElement = document.activeElement;
    const focusWasMovedElsewhere =
      activeElement instanceof HTMLElement &&
      activeElement !== document.body &&
      activeElement !== textarea;

    if (!textarea || textarea.disabled || focusWasMovedElsewhere) {
      return;
    }

    textarea.focus({ preventScroll: true });
    shouldRestoreTextareaFocusRef.current = false;
  }, [isInteractive, resolveTextarea, restoreFocusAfterSubmit]);

  const handleRemoveTarget = useCallback(
    (target: AnalysisTarget) => {
      const targetKey = getAnalysisTargetKey(target);
      if (
        pageAnalysisTarget &&
        getAnalysisTargetKey(pageAnalysisTarget) === targetKey
      ) {
        suppressPageContextAutoSyncOnCurrentPath();
      }
      removeAnalysisTarget(target);
      removeMentionTarget(target);
    },
    [
      pageAnalysisTarget,
      removeAnalysisTarget,
      removeMentionTarget,
      suppressPageContextAutoSyncOnCurrentPath,
    ],
  );

  const contextValue = useMemo<ChatPromptSurfaceContextValue>(
    () => ({
      input,
      setInput,
      isExpanded,
      canSubmit,
      isAttachmentDropActive,
      isInteractive,
      optionsState,
      analysisTargets,
      pageAnalysisTarget,
      manualAnalysisTargets,
      addAnalysisTarget,
      mentionedTargets,
      mentionInstances,
      mentionMatch,
      mentionAnchor,
      mentionMenuOpen,
      mentionActiveOptionKey,
      setMentionActiveOptionKey,
      handleMentionTargetSelect,
      handleTextareaChange,
      handleTextareaKeyDown,
      handleTextareaKeyUp,
      handleTextareaClick,
      textareaElement,
      bindTextarea,
      optionMetadataByKey,
      showInputOverlay,
      handleRemoveTarget,
      handleSubmit,
      attachments,
      attachmentErrors,
      fileInputId,
      attachmentInputRef,
      handleAttachmentButtonClick,
      handleAttachmentChange,
      handleAttachmentDragEnter,
      handleAttachmentDragLeave,
      handleAttachmentDragOver,
      handleAttachmentDrop,
      handleAttachmentRemove,
      handleAttachmentErrorDismiss,
      handleAttachmentPaste,
      textareaRows,
      model,
      highQuotaIndicator,
      lowQuotaIndicator,
      setContextPickerOpen,
      setModelSelectOpen,
      handleModelValueChange,
      speechSupported,
      speechIsListening,
      speechInterimTranscript,
      toggleSpeech,
    }),
    [
      addAnalysisTarget,
      analysisTargets,
      attachmentErrors,
      attachments,
      canSubmit,
      fileInputId,
      handleAttachmentButtonClick,
      handleAttachmentChange,
      handleAttachmentDragEnter,
      handleAttachmentDragLeave,
      handleAttachmentDragOver,
      handleAttachmentDrop,
      handleAttachmentErrorDismiss,
      handleAttachmentPaste,
      handleAttachmentRemove,
      handleMentionTargetSelect,
      handleModelValueChange,
      handleRemoveTarget,
      handleSubmit,
      handleTextareaChange,
      handleTextareaClick,
      handleTextareaKeyDown,
      handleTextareaKeyUp,
      bindTextarea,
      highQuotaIndicator,
      input,
      isAttachmentDropActive,
      isExpanded,
      isInteractive,
      lowQuotaIndicator,
      manualAnalysisTargets,
      mentionActiveOptionKey,
      mentionAnchor,
      mentionInstances,
      mentionMatch,
      mentionMenuOpen,
      mentionedTargets,
      model,
      optionMetadataByKey,
      optionsState,
      pageAnalysisTarget,
      setContextPickerOpen,
      setMentionActiveOptionKey,
      setModelSelectOpen,
      showInputOverlay,
      speechSupported,
      speechIsListening,
      speechInterimTranscript,
      toggleSpeech,
      textareaElement,
      textareaRows,
    ],
  );

  return (
    <ChatPromptSurfaceContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className={cn(
          className,
          isExpanded ? expandedClassName : compactClassName,
        )}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
      >
        {children}
        <AiModelUpgradeDialog
          open={isUpgradeDialogOpen}
          onOpenChange={setUpgradeDialogOpen}
          aiModelAccess={aiModelAccess}
        />
      </div>
    </ChatPromptSurfaceContext.Provider>
  );
}
